import { Fragment } from "react";
import { DIFF_SECTION_LABELS, type DiffSection, type ModelDiff } from "@shared/roi/diff";
import { Badge, type BadgeKind } from "@client/components/Badge";
import { formatDiffValue } from "@client/lib/diffFormat";

/**
 * What changed between two models, field by field.
 *
 * Used twice with the same props: the version screen puts a snapshot beside the
 * current model, and the comparison screen puts two scenarios beside each other.
 * The shared `diffModels` does the thinking; this only has to render values,
 * which is why it can afford to be this literal.
 *
 * Three columns, not a red/green gutter. A diff of *numbers* is read by putting
 * the two figures next to each other — "18,000 → 21,000" — and a line-based
 * presentation borrowed from source control would bury that in punctuation.
 */

const CHANGE_BADGE: Record<
  ModelDiff["entries"][number]["change"],
  { label: string; kind: BadgeKind }
> = {
  added: { label: "Added", kind: "positive" },
  removed: { label: "Removed", kind: "warning" },
  changed: { label: "Changed", kind: "info" },
};

interface DiffTableProps {
  diff: ModelDiff;
  currencyCode: string;
  /** Column headings — "Version 7" and "Version 9", or two scenario names. */
  beforeLabel: string;
  afterLabel: string;
  /** Shown instead of the table when nothing differs. */
  emptyMessage?: string;
}

const SECTION_ORDER: DiffSection[] = ["settings", "packages", "lineItems", "adoption"];

export function DiffTable({
  diff,
  currencyCode,
  beforeLabel,
  afterLabel,
  emptyMessage = "These two are identical — every input matches.",
}: DiffTableProps) {
  if (diff.total === 0) {
    return (
      <p
        role="status"
        className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-soft"
      >
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="-mx-2 overflow-x-auto px-2">
      <table className="w-full min-w-[40rem] border-collapse text-sm">
        <caption className="sr-only">
          {`Differences between ${beforeLabel} and ${afterLabel}: ${diff.total} field${diff.total === 1 ? "" : "s"}`}
        </caption>
        <thead className="border-b border-line">
          <tr>
            <th
              scope="col"
              className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft"
            >
              Field
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-ink-soft"
            >
              {beforeLabel}
            </th>
            <th
              scope="col"
              className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-ink-soft"
            >
              {afterLabel}
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              <span className="sr-only">Kind of change</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {SECTION_ORDER.filter((section) => diff.counts[section] > 0).map((section) => (
            <Fragment key={section}>
              <tr className="bg-chip-bg">
                <th
                  scope="colgroup"
                  colSpan={4}
                  className="px-3 py-1.5 text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft"
                >
                  {DIFF_SECTION_LABELS[section]}
                  <span className="tabular ml-1.5 font-semibold normal-case tracking-normal">
                    ({diff.counts[section]})
                  </span>
                </th>
              </tr>

              {diff.entries
                .filter((entry) => entry.section === section)
                .map((entry) => {
                  const badge = CHANGE_BADGE[entry.change];
                  return (
                    <tr key={entry.key} className="border-line/60 border-b last:border-0">
                      <th scope="row" className="px-3 py-2 text-left font-normal">
                        <span className="font-semibold text-ink">{entry.field}</span>
                        {/* The subject repeats on every row on purpose: a table
                            people scan for one package must not need its section
                            heading read back to make sense. */}
                        <span className="ml-1.5 text-xs text-ink-soft">{entry.subject}</span>
                      </th>
                      <td className="tabular whitespace-nowrap px-3 py-2 text-right text-ink-soft">
                        {formatDiffValue(entry.before, currencyCode)}
                      </td>
                      <td className="tabular whitespace-nowrap px-3 py-2 text-right font-semibold text-ink">
                        {formatDiffValue(entry.after, currencyCode)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Badge kind={badge.kind}>{badge.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
